import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Download, List, LayoutGrid, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { download } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApi, PageHeader, Loading, Empty, StatusBadge, Select, Pager, TableWrap } from "../components/common";
import { ProjectForm } from "../components/ProjectForms";
import { useDirectory } from "../components/LeadForm";
import { Button } from "../components/ui/button";
import { fdate, inr, todayIST } from "../lib/format";

const BOARD = ["New", "Assigned", "Designing", "Review", "Corrections", "Final Approval", "Completed", "Delivered", "On Hold"];

export function Calendar() {
  const [month, setMonth] = useState(todayIST().slice(0, 7));
  const { data } = useApi(`/calendar?month=${month}`);
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1).getDay();
  const days = new Date(y, m, 0).getDate();
  const shift = (n) => { const d = new Date(y, m - 1 + n, 1); setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); };
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div className="vmw-card p-4" data-testid="project-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="h-5 w-5" /></button>
        <span className="font-display font-semibold">{new Date(y, m - 1).toLocaleString("en-IN", { month: "long", year: "numeric" })}</span>
        <button onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="h-5 w-5" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[11px]">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="text-center font-semibold text-muted-foreground py-1">{d}</div>)}
        {cells.map((d, i) => {
          const key = d && `${month}-${String(d).padStart(2, "0")}`;
          const ps = d ? data?.projects.filter((p) => p.deadline === key) || [] : [];
          const ts = d ? data?.tasks.filter((t) => t.due_date === key) || [] : [];
          return (
            <div key={i} className={`min-h-[84px] rounded-md border p-1 ${d ? "bg-white" : "bg-transparent border-transparent"} ${key === todayIST() ? "ring-2 ring-primary" : ""}`}>
              {d && <div className="font-semibold text-slate-500">{d}</div>}
              {ps.map((p) => <Link key={p.id} to={`/projects/${p.id}`} className="block truncate rounded px-1 mt-0.5 bg-violet-100 text-violet-800">{p.name}</Link>)}
              {ts.slice(0, 3).map((t) => <Link key={t.id} to={`/projects/${t.project_id}`} className="block truncate rounded px-1 mt-0.5 bg-amber-50 text-amber-800">{t.name}</Link>)}
            </div>);
        })}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-muted-foreground"><span><span className="inline-block h-2 w-2 rounded bg-violet-300 mr-1" />Project deadline</span><span><span className="inline-block h-2 w-2 rounded bg-amber-300 mr-1" />Task due</span></div>
    </div>
  );
}

export default function Projects() {
  const { user, meta } = useAuth();
  const [view, setView] = useState("list");
  const [flt, setFlt] = useState({ q: "", status: "", designer: "", priority: "", overdue: false });
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const designers = useDirectory("designer");
  const qs = new URLSearchParams({ ...Object.fromEntries(Object.entries(flt).filter(([, v]) => v !== "" && v !== false)), page: view === "board" ? 1 : page, page_size: view === "board" ? 300 : 25 }).toString();
  const { data, loading, reload } = useApi(`/projects?${qs}`);
  const set = (k) => (v) => { setFlt({ ...flt, [k]: v }); setPage(1); };
  const owner = user.role === "owner";
  const t = todayIST();
  const tabs = [["list", List], ["board", LayoutGrid], ["calendar", CalendarDays]];
  return (
    <div>
      <PageHeader title="Projects" subtitle={user.role === "designer" ? "Projects assigned to you" : "Album production tracker"} actions={<>
        <div className="flex rounded-lg border bg-white p-0.5">{tabs.map(([v, I]) => (
          <button key={v} data-testid={`project-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1 capitalize ${view === v ? "vmw-gradient text-white" : "text-slate-600"}`}><I className="h-4 w-4" />{v}</button>))}</div>
        <Button variant="outline" onClick={() => download("/export/projects", "projects.csv")} data-testid="project-export-button"><Download className="h-4 w-4 mr-1" />Export</Button>
        {owner && <Button className="vmw-gradient" onClick={() => setOpen(true)} data-testid="project-add-button"><Plus className="h-4 w-4 mr-1" />New Project</Button>}</>} />
      {view !== "calendar" && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <input data-testid="project-filter-search" className="vmw-input col-span-2" placeholder="Search project, client, ID…" value={flt.q} onChange={(e) => set("q")(e.target.value)} />
          <Select testid="project-filter-status" placeholder="All statuses" options={[{ value: "active", label: "Active only" }, ...(meta?.project_statuses || [])]} value={flt.status} onChange={set("status")} />
          {owner ? <Select testid="project-filter-designer" placeholder="All designers" options={designers.map((d) => ({ value: d.id, label: d.name }))} value={flt.designer} onChange={set("designer")} />
            : <Select testid="project-filter-priority" placeholder="Any priority" options={meta?.priorities || []} value={flt.priority} onChange={set("priority")} />}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="project-filter-overdue" checked={flt.overdue} onChange={(e) => set("overdue")(e.target.checked)} />Overdue only</label>
        </div>
      )}
      {view === "calendar" ? <Calendar /> : loading && !data ? <Loading /> : view === "board" ? (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="project-board">
          {BOARD.map((s) => { const items = data.items.filter((p) => p.status === s); return (
            <div key={s} className="kanban-col shrink-0 rounded-xl bg-muted/60 p-2.5">
              <div className="flex justify-between px-1 mb-2 text-xs font-bold uppercase tracking-wider text-slate-600"><span>{s}</span><span className="text-primary">{items.length}</span></div>
              <div className="space-y-2">{items.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="block bg-white border rounded-lg p-3 hover:border-primary">
                  <div className="text-sm font-semibold truncate">{p.name}</div><div className="text-xs text-muted-foreground truncate">{p.client_name}</div>
                  <div className="flex justify-between mt-2 text-xs"><span>{p.total_sheets} sheets</span><span className={p.deadline && p.deadline < t && !["Completed", "Delivered"].includes(p.status) ? "text-rose-600 font-semibold" : ""}>{fdate(p.deadline)}</span></div>
                  <div className="h-1 rounded bg-muted mt-2 overflow-hidden"><div className="h-full vmw-gradient" style={{ width: `${p.task_total ? (p.task_done / p.task_total) * 100 : 0}%` }} /></div>
                </Link>))}</div>
            </div>); })}
        </div>
      ) : (
        <TableWrap>
          {!data.items.length ? <Empty text="No projects" /> : (
            <table className="vmw-table" data-testid="projects-table">
              <thead><tr><th>Project</th><th>Client</th><th>Album</th><th>Designer</th><th>Deadline</th><th>Progress</th><th>Priority</th><th>Status</th>{owner && <th>Value</th>}</tr></thead>
              <tbody>{data.items.map((p) => (
                <tr key={p.id} data-testid={`project-row-${p.id}`}>
                  <td><Link to={`/projects/${p.id}`} className="font-medium hover:text-primary">{p.name}</Link><div className="font-mono text-[11px] text-muted-foreground">{p.code}</div></td>
                  <td>{p.client_name}<div className="text-xs text-muted-foreground">{p.studio_name}</div></td>
                  <td className="whitespace-nowrap">{p.album_size || "—"} · {p.total_sheets} sh</td>
                  <td className="text-xs">{p.designer_names?.join(", ") || "—"}</td>
                  <td className={p.deadline && p.deadline < t && !["Completed", "Delivered", "Canceled"].includes(p.status) ? "text-rose-600 font-semibold" : ""}>{fdate(p.deadline)}</td>
                  <td className="text-xs">{p.task_done}/{p.task_total} tasks</td>
                  <td><StatusBadge value={p.priority} /></td><td><StatusBadge value={p.status} /></td>
                  {owner && <td className="font-num">{inr(p.deal_value)}</td>}
                </tr>))}</tbody>
            </table>)}
          <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />
        </TableWrap>
      )}
      <ProjectForm open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}
